import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  PlusIcon,
  CalendarIcon,
  TagIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../../services/apiService';

const COLUMNS = [
  { id: 1, label: 'To Do',      statusValue: 1, color: 'border-gray-300' },
  { id: 2, label: 'In Progress', statusValue: 2, color: 'border-blue-400' },
  { id: 3, label: 'Done',        statusValue: 3, color: 'border-green-400' },
  { id: 4, label: 'Blocked',     statusValue: 4, color: 'border-red-500' },
];

function TaskCard({ task, index, onEdit }) {
  const isBlocked = task.status === 4;
  return (
    <Draggable draggableId={String(task.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onEdit(task)}
          className={`bg-white rounded-md border p-3 mb-2 cursor-pointer select-none shadow-sm hover:shadow-md transition-shadow
            ${snapshot.isDragging ? 'shadow-lg rotate-1 opacity-90' : ''}
            ${isBlocked ? 'border-red-300' : 'border-gray-200'}`}
        >
          <div className="text-sm font-medium text-gray-900 leading-tight">{task.title}</div>
          {task.tag && (
            <div className="flex items-center gap-1 mt-1.5">
              <TagIcon className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{task.tag}</span>
            </div>
          )}
          {isBlocked && task.blockerReason && (
            <div className="flex items-start gap-1 mt-1.5">
              <LockClosedIcon className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-red-600 line-clamp-2">{task.blockerReason}</span>
            </div>
          )}
          <div className="flex items-center justify-between mt-2">
            {task.assignedToName ? (
              <div className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold">
                {task.assignedToName[0].toUpperCase()}
              </div>
            ) : <div />}
            {task.dueDate && (
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <CalendarIcon className="h-3 w-3" />
                {new Date(task.dueDate).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}

function QuickAddRow({ columnStatus, projectId, phaseId, onAdded, onClose }) {
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const task = await apiService.createProjectTask(projectId, {
        title: title.trim(),
        status: columnStatus,
        phaseId: phaseId || null,
      });
      onAdded(task);
      setTitle('');
    } catch (err) {
      alert('Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-2">
      <input
        autoFocus
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Task title…"
        className="w-full px-2 py-1.5 text-sm border border-blue-400 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <div className="flex gap-2 mt-1.5">
        <button type="submit" disabled={saving || !title.trim()}
          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50">
          Add
        </button>
        <button type="button" onClick={onClose}
          className="px-3 py-1 text-gray-600 text-xs rounded-md hover:bg-gray-100">
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function KanbanView({ projectId, tasks, setTasks, phaseId, onEditTask }) {
  const [addingTo, setAddingTo] = useState(null); // column statusValue

  const tasksByStatus = COLUMNS.reduce((acc, col) => {
    acc[col.statusValue] = tasks
      .filter(t => t.status === col.statusValue)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    return acc;
  }, {});

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const taskId = parseInt(draggableId);
    const newStatus = parseInt(destination.droppableId);
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    // Optimistically update UI
    const updatedTasks = tasks.map(t =>
      t.id === taskId ? { ...t, status: newStatus, sortOrder: destination.index } : t
    );
    setTasks(updatedTasks);

    try {
      await apiService.updateProjectTask(projectId, taskId, {
        status: newStatus,
        sortOrder: destination.index,
      });
    } catch (err) {
      setTasks(tasks); // revert on error
    }
  };

  const handleTaskAdded = (task) => {
    setTasks(prev => [...prev, task]);
    setAddingTo(null);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {COLUMNS.map(col => {
          const colTasks = tasksByStatus[col.statusValue] || [];
          const isBlocked = col.statusValue === 4;
          return (
            <div key={col.id}
              className={`bg-gray-50 rounded-lg border-t-4 ${isBlocked ? 'border-t-red-500' : col.color.replace('border-', 'border-t-')} p-3 min-h-[200px]`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">{col.label}</span>
                  <span className="text-xs bg-white text-gray-500 border border-gray-200 px-1.5 py-0.5 rounded-full">
                    {colTasks.length}
                  </span>
                </div>
                <button
                  onClick={() => setAddingTo(col.statusValue)}
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                  title="Add task"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>

              <Droppable droppableId={String(col.statusValue)}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`min-h-[100px] transition-colors ${snapshot.isDraggingOver ? 'bg-blue-50 rounded-md' : ''}`}
                  >
                    {colTasks.map((task, index) => (
                      <TaskCard key={task.id} task={task} index={index} onEdit={onEditTask} />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

              {addingTo === col.statusValue ? (
                <QuickAddRow
                  columnStatus={col.statusValue}
                  projectId={projectId}
                  phaseId={phaseId}
                  onAdded={handleTaskAdded}
                  onClose={() => setAddingTo(null)}
                />
              ) : (
                <button
                  onClick={() => setAddingTo(col.statusValue)}
                  className="w-full mt-2 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 py-1"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  Add task
                </button>
              )}
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
